import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { Button } from '@framework/ui/atoms/Button';
import { Input } from '@framework/ui/atoms/Input';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { useActionLogSender } from '@framework/action-log';
import { ViewModelOperation } from '@view-model/domain/view-model';

type Props = {
  isOpen: boolean;
  folder: Folder;
  onClose(): void;
};

export const FolderEditModal: React.FC<Props> = ({ isOpen, folder, onClose }: Props) => {
  const { t } = useTranslation();
  const actionLogSender = useActionLogSender();

  const [name, setName] = useState<string>('');
  const [canSave, setCanSave] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setName(folder.name);
    }
  }, [folder.name, isOpen]);

  useEffect(() => {
    setCanSave(name.trim() !== '');
  }, [name]);

  const handleSubmit = useCallback(async () => {
    await ViewModelOperation.updateFolder(folder.id, name, actionLogSender);
    onClose();
    setName('');
  }, [folder.id, name, actionLogSender, onClose]);

  const handleClose = useCallback(() => {
    setName('');
    onClose();
  }, [onClose]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.folderEdit.title')}
      submitButton={
        <Button onClick={handleSubmit} disabled={!canSave}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button onClick={handleClose} color={'secondary'}>
          {t('common.action.cancel')}
        </Button>
      }
    >
      <div className="p-2">
        <Input
          type="text"
          placeholder={t('workspace.folderCreate.namePlaceholder')}
          value={name}
          onChange={handleChange}
          autoFocus={true}
        />
      </div>
    </GeneralModal>
  );
};
